/**
 * Realtime WebSocket client. Connects to /v1/realtime, subscribes to
 * site/{id}/forklifts and the active asset's pose channel, and pushes deltas
 * into the telemetry store.
 *
 * The server may emit:
 *   { type: "delta",    channel, data: { asset_id, ts, x, y, yaw, speed, loaded } }
 *   { type: "gap",      asset_id, from, to }
 *   { type: "alert",    data: {...} }
 *   { type: "snapshot", channel, data: [...] }
 *   { type: "pong" }
 */
import { useTelemetryStore } from "../stores/telemetryStore";
import { useSiteStore } from "../stores/siteStore";

export interface ConnectOptions {
  url?: string;
  siteId?: string;
  token?: string;
}

/**
 * Transport state, surfaced so the shell can stop claiming "AO VIVO" over a dead
 * socket (ADR 224).
 *
 * `online` means the WebSocket is OPEN — the transport is up. It deliberately
 * does NOT mean data is arriving: an idle site with no moving forklifts is
 * silent and still perfectly connected, so treating silence as failure would
 * cry wolf every night shift.
 *
 * Backoff counts as `offline`, not `connecting`. During an outage we spend most
 * of the cycle waiting, and a badge that reads "CONECTANDO" for minutes while
 * nothing reconnects is the same lie in a different font. `connecting` is only
 * the window between constructing a socket and its open event.
 *
 * `idle` is "not connected because there is nothing to connect to" — no site in
 * scope, so no channel to subscribe to (ADR 225). It is NOT a failure and must
 * not be shown as one: a grant-less user has nothing wrong with their network.
 */
export type RealtimeStatus = "idle" | "connecting" | "online" | "offline";

/** RFC 6455 close code the server sends on a tenant violation. */
const POLICY_VIOLATION = 1008;

export class RealtimeClient {
  private url: string;
  private token?: string;
  private ws?: WebSocket;
  private retryMs = 500;
  private timer?: number;
  private pingTimer?: number;
  private fleetWarehouseId?: string;
  private unwatchStore?: () => void;
  // Set on disconnect() so the close-event handler doesn't schedule a reconnect.
  private _stopped = false;
  private status: RealtimeStatus = "idle";
  private listeners = new Set<() => void>();
  // Set when the server closes us for a tenant violation. Retrying a permanent
  // authorization refusal can only ever produce the same answer, so we stop.
  private policyDenied = false;

  constructor(opts: ConnectOptions = {}) {
    this.url    = opts.url ?? this.defaultUrl();
    // siteId is resolved fresh from the store on each connect() call; opts.siteId overrides.
    if (opts.siteId) useSiteStore.getState().setActiveSiteId(opts.siteId);
    this.token  = opts.token;
  }

  /** Current transport state. Arrow property: useSyncExternalStore needs a stable
   *  reference, and a plain method would be rebound on every render. */
  getStatus = (): RealtimeStatus => this.status;

  /** Subscribe to status changes; returns the unsubscribe. */
  subscribe = (onChange: () => void): (() => void) => {
    this.listeners.add(onChange);
    return () => { this.listeners.delete(onChange); };
  };

  private setStatus(next: RealtimeStatus): void {
    if (this.status === next) return;   // no-op transitions must not wake React
    this.status = next;
    for (const fn of this.listeners) fn();
  }

  private defaultUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/v1/realtime`;
  }

  connect(): void {
    this._stopped = false;
    this.policyDenied = false;
    const siteId = useSiteStore.getState().activeSiteId;
    const effectiveToken = this.token ?? useSiteStore.getState().token;
    try {
      // B4 (ADR 91): the JWT rides the Sec-WebSocket-Protocol header
      // (["bearer", token]) — never the URL, so nothing lands in access logs.
      // The server advertises "bearer" back (SubProtocolCapable), which the
      // browser requires before it will keep the connection open.
      this.ws = effectiveToken
        ? new WebSocket(this.url, ["bearer", effectiveToken])
        : new WebSocket(this.url);
    } catch (e) {
      console.warn("ws construct failed", e);
      this.setStatus("offline");
      this.scheduleReconnect();
      return;
    }
    this.setStatus("connecting");

    this.ws.addEventListener("open", () => {
      this.retryMs = 500;
      this.setStatus("online");
      // Explicit-warehouse rule (retrospective C5): never subscribe to an
      // invented wh-{siteId}. Subscribe once the store has the real id and
      // follow it — the previous open-time-only read raced the store load and
      // could stay on a made-up channel for the whole session.
      this.fleetWarehouseId = undefined;
      this.subscribeFleet(siteId, useSiteStore.getState().activeWarehouseId);
      this.unwatchStore?.();
      this.unwatchStore = useSiteStore.subscribe((s) =>
        this.subscribeFleet(siteId, s.activeWarehouseId));
      this.send({ type: "sub", channel: `site/${siteId}/alerts` });
      // ADR 324 — Mode I1. Site-scoped, not warehouse-scoped: a run announces which
      // warehouse it is sweeping in its own deltas, and the tab should not have to
      // resubscribe to notice one starting in the warehouse it is already looking at.
      this.send({ type: "sub", channel: `site/${siteId}/inventory` });
      this.pingTimer = window.setInterval(() => this.send({ type: "ping" }), 25_000);
    });
    this.ws.addEventListener("message", (e) => this.onMessage(e.data));
    this.ws.addEventListener("close", (e) => {
      if ((e as CloseEvent)?.code === POLICY_VIOLATION) {
        // The server kills the session when a subscription is refused. Blind
        // retry turns one refusal into a permanent storm — every attempt costs
        // it a JWT decode and a grant lookup (ADR 225).
        this.policyDenied = true;
        console.warn("realtime: subscription refused (tenant scope); not retrying");
      }
      this.setStatus("offline");
      if (!this._stopped && !this.policyDenied) this.scheduleReconnect();
    });
    this.ws.addEventListener("error", () => this.ws?.close());
  }

  disconnect(): void {
    this._stopped = true;
    if (this.timer)     clearTimeout(this.timer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.unwatchStore?.();
    this.unwatchStore = undefined;
    this.ws?.close();
    this.ws = undefined;
    // A socket that never opened fires no close event, so set it here too.
    // `idle`, not `offline`: a deliberate stop is not a failure.
    this.setStatus("idle");
  }

  /** (Re)subscribe the warehouse-scoped fleet channel to match the store. */
  private subscribeFleet(siteId: string, warehouseId: string | undefined): void {
    if (warehouseId === this.fleetWarehouseId) return;
    if (this.fleetWarehouseId) {
      this.send({ type: "unsub", channel: `site/${siteId}/warehouse/${this.fleetWarehouseId}/forklifts` });
    }
    this.fleetWarehouseId = warehouseId;
    if (warehouseId) {
      this.send({ type: "sub", channel: `site/${siteId}/warehouse/${warehouseId}/forklifts` });
    }
  }

  private scheduleReconnect(): void {
    if (this._stopped) return;
    if (this.pingTimer) clearInterval(this.pingTimer);
    const ms = Math.min(this.retryMs, 8_000) + Math.random() * 200;
    this.retryMs = Math.min(this.retryMs * 2, 8_000);
    this.timer = window.setTimeout(() => this.connect(), ms);
  }

  private send(o: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(o));
  }

  private onMessage(raw: string): void {
    let msg: any;
    try { msg = JSON.parse(raw); } catch { return; }
    const store = useTelemetryStore.getState();
    if (msg.type === "delta" && msg.data) {
      store.applyDelta({
        id: msg.data.asset_id,
        x: msg.data.x,
        y: msg.data.y,
        yaw: msg.data.yaw,
        speed: msg.data.speed,
        loaded: msg.data.loaded,
        battery: typeof msg.data.battery === "number" ? msg.data.battery : undefined,
        lastSeenMs: msg.data.ts ?? Date.now(),
        series: useTelemetryStore.getState().forklifts[msg.data.asset_id]?.series ?? "F",
      });
    } else if (msg.type === "gap") {
      store.applyGap({ assetId: msg.asset_id, fromMs: msg.from, toMs: msg.to });
    } else if (msg.type === "gateway_status") {
      store.applyGatewayStatus({
        id:          msg.id as string,
        assetId:     msg.assetId as string | undefined,
        online:      msg.online as boolean,
        lastSeenMs:  msg.lastSeenMs as number,
        peripherals: msg.peripherals
          ? {
              v2pro: msg.peripherals.v2pro
                ? {
                    ok:           msg.peripherals.v2pro.ok as boolean,
                    lastPoseAgeMs: msg.peripherals.v2pro.last_pose_age_ms as number | undefined,
                  }
                : undefined,
              forkSensor: msg.peripherals.fork_sensor
                ? {
                    ok:   msg.peripherals.fork_sensor.ok as boolean,
                    type: msg.peripherals.fork_sensor.type as string | undefined,
                  }
                : undefined,
              bufferFill: msg.peripherals.buffer_fill as number | undefined,
            }
          : undefined,
        // ADR 365
        ic3: msg.ic3 && typeof msg.ic3 === "object"
          ? {
              kitWsOk:          msg.ic3.kit_ws_ok === true,
              localized:        msg.ic3.localized === true,
              runPhase:         typeof msg.ic3.run_phase === "string" ? msg.ic3.run_phase : "idle",
              outboxPending:    typeof msg.ic3.outbox_pending === "number" ? msg.ic3.outbox_pending : 0,
              outboxOldestAgeS: typeof msg.ic3.outbox_oldest_age_s === "number" ? msg.ic3.outbox_oldest_age_s : null,
              evidencePending:  typeof msg.ic3.evidence_pending === "number" ? msg.ic3.evidence_pending : 0,
              lastUploadOkTs:   typeof msg.ic3.last_upload_ok_ts === "number" ? msg.ic3.last_upload_ok_ts : null,
              lastUploadError:  typeof msg.ic3.last_upload_error === "string" ? msg.ic3.last_upload_error : null,
            }
          : undefined,
      });
    } else if (msg.type === "snapshot" && Array.isArray(msg.data)) {
      for (const row of msg.data) {
        store.applyDelta({
          id: row.asset_id, x: row.x, y: row.y, yaw: row.yaw,
          speed: row.speed, loaded: row.loaded,
          battery: typeof row.battery === "number" ? row.battery : undefined,
          series: useTelemetryStore.getState().forklifts[row.asset_id]?.series ?? "F",
        });
      }
    } else if (msg.type === "inventory_activity" && msg.data) {
      // Addresses only, by contract (ADR 324): there is no product or quantity here to
      // read, and there must never be — mid-run association can be overturned by the
      // next read, so live counts would be a flip-flopping dashboard (seam 16).
      store.applyInventoryActivity({
        siteId:      msg.data.siteId ?? "",
        warehouseId: msg.data.warehouseId ?? null,
        runId:       msg.data.runId ?? null,
        addresses:   Array.isArray(msg.data.addresses) ? msg.data.addresses : [],
        tsMs:        typeof msg.data.tsMs === "number" ? msg.data.tsMs : Date.now(),
        poseX:       typeof msg.data.poseX === "number" ? msg.data.poseX : null,
        poseY:       typeof msg.data.poseY === "number" ? msg.data.poseY : null,
        firstTsMs:   typeof msg.data.firstTsMs === "number" ? msg.data.firstTsMs : null,
        snapshot:    msg.data.snapshot === true,
      });
    } else if (msg.type === "alert" && msg.data) {
      store.applyAlertDelta({
        id:       msg.data.id       ?? msg.data.alertId ?? "",
        siteId:   msg.data.siteId   ?? msg.data.site_id ?? "",
        assetId:  msg.data.assetId  ?? msg.data.asset_id ?? "",
        code:     msg.data.code     ?? "",
        severity: msg.data.severity ?? "INFO",
        ts:       msg.data.ts ? new Date(msg.data.ts).getTime() : Date.now(),
        status:   msg.data.status   ?? "open",
        detail:   msg.data.detail,
      });
    }
  }
}

export const realtime = new RealtimeClient();
